// src/admin/CustomerManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../firebase';
import {
  Users, UserPlus, Edit, Trash2, X, Search, Mail, Phone,
  MapPin, Eye, ShoppingBag
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const firebaseConfig = {
  apiKey: "AIzaSyASOCPRbKYy3oQBscJEYNCHp5jC0kSTV0Q",
  authDomain: "organic-store-5d667.firebaseapp.com",
  projectId: "organic-store-5d667",
  storageBucket: "organic-store-5d667.firebasestorage.app",
  messagingSenderId: "951551672704",
  appId: "1:951551672704:web:0fce549e67798afe26c165"
};

// ❌ Ye roles KABHI customer list mein nahi dikhne chahiye
const NON_CUSTOMER_ROLES = [
  'superghost',
  'superadmin',
  'admin',
  'manager',
  'employee',
];

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  const defaultFormData = {
    name: '', email: '', phone: '', password: '',
    city: '', address: '', state: '', postalCode: '',
  };
  const [formData, setFormData] = useState(defaultFormData);

  const fetchData = async () => {
    setLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // ✅ Sirf 'customer' role wale ya role-less users ko dikhao
      const realCustomers = allUsers.filter(u => {
        if (!u.role) return true;
        if (u.role === 'customer') return true;
        if (NON_CUSTOMER_ROLES.includes(u.role)) return false;
        return true;
      });

      setCustomers(realCustomers);

      const ordersSnap = await getDocs(collection(db, 'orders'));
      setOrders(ordersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCustomerOrders = (email) => {
    return orders.filter(o =>
      o.customerEmail === email ||
      o.email === email
    );
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.email || !formData.password) {
      setMessage({ type: 'error', text: 'Name, Email aur Password zaroori hain.' });
      return;
    }
    if (formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password kam az kam 6 characters ka hona chahiye.' });
      return;
    }

    try {
      const secondaryApp = initializeApp(firebaseConfig, 'SecondaryCustomer');
      const secondaryAuth = getAuth(secondaryApp);

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, formData.email, formData.password
      );
      const newUserId = userCredential.user.uid;

      await secondaryAuth.signOut();
      await deleteApp(secondaryApp);

      await setDoc(doc(db, 'users', newUserId), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        city: formData.city || '',
        address: formData.address || '',
        state: formData.state || '',
        postalCode: formData.postalCode || '',
        role: 'customer',   // ✅ Always customer
        createdAt: new Date(),
      });

      setMessage({ type: 'success', text: 'Customer created!' });
      fetchData();
      setTimeout(() => {
        setShowModal(false);
        setFormData(defaultFormData);
      }, 1500);
    } catch (error) {
      console.error('Create error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setMessage({ type: 'error', text: 'Ye email pehle se registered hai.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to create customer.' });
      }
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, 'users', editingCustomer.id), {
        name: formData.name,
        phone: formData.phone || '',
        city: formData.city || '',
        address: formData.address || '',
        state: formData.state || '',
        postalCode: formData.postalCode || '',
      });
      setMessage({ type: 'success', text: 'Customer updated!' });
      fetchData();
      setTimeout(() => { setShowModal(false); setEditingCustomer(null); }, 1500);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update customer.' });
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete customer "${customer.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'users', customer.id));
      setShowSuccess(true);
      fetchData();
    } catch (error) {
      alert('Failed to delete customer.');
    }
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData(defaultFormData);
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      password: '',
      city: customer.city || '',
      address: customer.address || '',
      state: customer.state || '',
      postalCode: customer.postalCode || '',
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openDetailModal = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailModal(true);
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customers Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            {customers.length} registered customers
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <UserPlus size={18} /> Add Customer
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="relative">
          <input type="text" placeholder="Search by name, email, ya phone..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Users size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No customers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Contact</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase hidden lg:table-cell">Location</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.map((customer) => {
                  const custOrders = getCustomerOrders(customer.email);
                  return (
                    <tr key={customer.id} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-green-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {customer.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{customer.name || 'No name'}</p>
                            <p className="text-xs text-gray-500 md:hidden">{customer.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600 hidden md:table-cell">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <Mail size={12} className="text-gray-400" /> {customer.email}
                          </span>
                          {customer.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={12} className="text-gray-400" /> {customer.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600 hidden lg:table-cell">
                        {customer.city ? (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} className="text-gray-400" /> {customer.city}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          custOrders.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {custOrders.length}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openDetailModal(customer)}
                            className="text-green-600 hover:bg-green-50 p-2 rounded-lg transition" title="View">
                            <Eye size={16} />
                          </button>
                          <button onClick={() => openEditModal(customer)}
                            className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition" title="Edit">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete(customer)}
                            className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={editingCustomer ? handleUpdateCustomer : handleCreateCustomer} className="p-5 space-y-4">
              {message.text && (
                <div className={`text-sm p-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    required />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!!editingCustomer}
                    className={`w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm ${
                      editingCustomer ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input type="tel" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                  <input type="text" value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                  <textarea value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                  <input type="text" value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Postal Code</label>
                  <input type="text" value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>

                {!editingCustomer && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Password *</label>
                    <input type="text" value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="Min 6 characters" required minLength={6} />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition">
                  {editingCustomer ? 'Update Customer' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Customer Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 bg-green-700 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  {selectedCustomer.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">{selectedCustomer.name}</h3>
                  <p className="text-xs text-gray-500">{selectedCustomer.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {selectedCustomer.phone && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone size={18} className="text-green-700" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-semibold">{selectedCustomer.phone}</p>
                    </div>
                  </div>
                )}
                {selectedCustomer.city && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin size={18} className="text-green-700" />
                    <div>
                      <p className="text-xs text-gray-500">City</p>
                      <p className="text-sm font-semibold">{selectedCustomer.city}</p>
                    </div>
                  </div>
                )}
                {selectedCustomer.address && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin size={18} className="text-green-700 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Full Address</p>
                      <p className="text-sm font-semibold">
                        {selectedCustomer.address}
                        {selectedCustomer.state && `, ${selectedCustomer.state}`}
                        {selectedCustomer.postalCode && ` - ${selectedCustomer.postalCode}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingBag size={18} className="text-green-700" />
                  <h4 className="font-bold text-green-900">Order History</h4>
                </div>
                {getCustomerOrders(selectedCustomer.email).length === 0 ? (
                  <p className="text-sm text-gray-500">No orders yet.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {getCustomerOrders(selectedCustomer.email).map(order => (
                      <div key={order.id} className="flex justify-between items-center p-2 bg-white rounded-lg text-sm">
                        <div>
                          <p className="font-semibold">#{order.id?.slice(-6)}</p>
                          <p className="text-xs text-gray-500">{order.status || 'Processing'}</p>
                        </div>
                        <p className="font-bold text-green-800">Rs. {order.total || 0}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="Customer deleted!"
        buttonText="Great!"
      />

    </div>
  );
};

export default CustomerManagement;